/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import ComponentBase from '../../ComponentBase';
import CLog, { LogTypes } from '../../ComponentLogger';
import { LifeCycle } from '../../LifeCycle';
import AssetScaler from '../AssetScaler';
import ReferencedComponent from '../ComponentReference/ReferencedComponent';
import OptionalComponent from '../Optional/OptionalComponent';
import SetNodeActiveComponent from './SetNodeActiveComponent';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

@cc._decorator.ccclass
export default abstract class ConfiguratorBaseComponent extends ComponentBase
{
    @property([cc.Prefab])
    // TODO: Move this to active list, so we can disable / enable.
    protected optionalInstantiations: cc.Prefab[] = [];

    protected abstract SetBindings(): void;

    protected scene: cc.Scene = null;
    protected setActiveMap: Map<LifeCycle, SetNodeActiveComponent[]> = new Map();

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this.SetBindings();

        this.scene = cc.director.getScene();

        if (CC_DEBUG)
        {
            const labels: cc.Label[] = this.scene.getComponentsInChildren(cc.Label);
            labels.forEach(label =>
            {
                if (label.getComponent(AssetScaler) || !label.font) { return; }

                label.addComponent(AssetScaler);

                let path: string = label.node.name;
                let current: cc.Node = label.node.parent;
                while (current)
                {
                    path = `${current.name}/${path}`;
                    current = current.parent;
                }

                CLog.warn(`No AssetScalar on label: ${path}`, LogTypes.Configuration);
            }, this);
        }

        this.configureSceneActiveNodes();
        this.setActive(LifeCycle.onLoad);
    }

    private configureOptionalPrefabs(): void
    {
        const canvas: cc.Node = cc.Canvas.instance.node || cc.director.getScene();

        this.optionalInstantiations.forEach((prefab, index) =>
            {
                const newNode: cc.Node = cc.instantiate(prefab);
                canvas.addChild(newNode);

                newNode.setPosition(new cc.Vec2(150, 150));

                // Add as optional component with Demo key.
                OptionalComponent.AddDemoOptionalComponent(newNode);
            }, this);
    }

    /** @hidden */
    protected start(): void
    {
        super.start();

        this.configureOptionalPrefabs();
        this.configureReferences();

        this.setActive(LifeCycle.start);
    }

    /** @hidden */
    protected firstUpdate(): void
    {
        this.setActive(LifeCycle.firstUpdate);
    }

    /**
     * Sets active components from map for a given lifecycle.
     * @param lifeCycle LifeCycle during which set active.
     */
    private setActive(lifeCycle: LifeCycle): void
    {
        const activeComponents: SetNodeActiveComponent[] = this.setActiveMap.get(lifeCycle);
        if (!activeComponents || activeComponents.length < 1) { return; }

        activeComponents.forEach(activeComponent => activeComponent.node.active = activeComponent.ToActive);
    }

    /** Fetches all referenced components and ensures they are mapped. */
    private configureReferences(): void
    {
        if (this.hasNoScene()) { return; }

        const refs: ReferencedComponent[] = this.scene.getComponentsInChildren(ReferencedComponent);
        if (refs) { refs.forEach(ref => ref.AddReference()); }
    }

    /** Fetches all [[SetNodeActiveComponent]] and adds to local map based on LifeCycle. */
    private configureSceneActiveNodes(): void
    {
        if (this.hasNoScene()) { return; }

        const nodeActiveComponents: SetNodeActiveComponent[]
            = this.scene.getComponentsInChildren(SetNodeActiveComponent);

        for (let activeComponent of nodeActiveComponents)
        {
            const components: cc.Component[] = this.setActiveMap.get(activeComponent.LifeCycle);
            if (!components)
            {
                this.setActiveMap.set(activeComponent.LifeCycle, [activeComponent]);
            }
            else
            {
                components.push(activeComponent);
            }
        }
    }

    /** @hidden */
    private hasNoScene(): boolean
    {
        if (!this.scene)
        {
            CLog.error("Could not load scene", this, LogTypes.Configuration);
            return true;
        }

        return false;
    }
}
